import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import MediaRelationsService from '../../services/mediarelations.services';
import { useCompanyId } from '../../services/CompanyidProvider';
import { LinearProgress, Box, Stack, Input, InputLabel, Modal, Popover, Paper, Typography, IconButton, Tooltip, Alert } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { FluxCheckbox, FluxButton } from '@meltwater/flux-web-components-react';
import csvDownload from 'json-to-csv-export';
import styles from './mediarelations.module.css';
import { getJwtToken } from '../../services/jwt.services';
import jwt_decode from 'jwt-decode';
import { useMediaRelationsData } from './mediarelations';
import DataTable from '../../components/DataTable';
import TableGuide from '../../components/TableGuide';

const MediaLists = ({ load, setActiveTab }) => {
  const [progress, setProgress] = useState(0);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('info');
  const [mediarelationspermissions, setMediarelationspermissions] = useState([]);
  const [copyToCompanyId, setCopyToCompanyId] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [copyListToCompanyModalOpen, setCopyListToCompanyModalOpen] = useState(false);
  const [mediaListsToCopy, setMediaListsToCopy] = useState([]);
  const [includePublic, setIncludePublic] = useState(null);
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [failedPages, setFailedPages] = useState([]);
  
  const { companyId } = useCompanyId();
  const { 
    medialistsData, 
    setMedialistsData,
    setLoading,
    apiAttempts,
    markApiAttempt,
    resetApiAttempt
  } = useMediaRelationsData();
  
  const loadingTimerRef = useRef(null);
  const landingPageNavRef = useRef(false);
  const isRequestInProgressRef = useRef(false);
  const isMountedRef = useRef(true);
  
  // Handle info button open/close
  const handleInfoClick = (event) => {
    setInfoAnchorEl(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoAnchorEl(null);
  };

  const openInfo = Boolean(infoAnchorEl);
  
  // User guide steps (add more as needed)
  const medialistsGuideSteps = useMemo(() => [
    {
      title: 'Media Lists Management',
      text: 'This guide will help you navigate the Media Lists features.',
      element: null,
    },
    {
      title: 'Search Media Lists',
      text: 'Use the search box to quickly find media lists by name or ID.',
      element: 'null',
    },
    {
      title: 'Column Visibility & Table Settings',
      text: 'Use these buttons to show or hide specific columns and adjust table settings.',
      element: 'null',
    },
    {
      title: 'Export Lists',
      text: 'You can export list overview data or contacts from selected lists using these buttons.',
      element: 'null',
    },
    {
      title: 'Include Public Contacts',
      text: 'Toggle this checkbox to include public contacts in exports.',
      element: 'null',
    },
    {
      title: 'Copy Contacts',
      text: 'Use this button to copy contacts between companies.',
      element: 'null',
    },
    {
      title: 'Information Icon',
      text: 'Click this icon for additional information about contact export and copying behavior.',
      element: 'null',
    }
  ], []);

  // Track initial load to identify direct navigation from landing page
  useEffect(() => {
    if (load && !landingPageNavRef.current) {
      landingPageNavRef.current = true;
    }
  }, [load]);

  const fetchData = useCallback(async () => {
    // Prevent multiple simultaneous requests
    if (isRequestInProgressRef.current) {
      console.log("Request already in progress, skipping duplicate call");
      return;
    }

    try {
      isRequestInProgressRef.current = true;
      setIsLoading(true);
      setLoading(true);
      setLoadingMessage('Fetching media lists...');
      loadingTimerRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          setLoadingMessage('Please wait, downloading lists may take some time...');
        }
      }, 10000);
    
      const response = await MediaRelationsService.getListsPage(companyId, 0);

      let totalPages = response.data.pages;
      const failedPages = [];

      try {
        for (let i = 1; i < totalPages; i++) {
          try {
            const pageResponse = await MediaRelationsService.getListsPage(companyId, i);
            response.data.hits.push(...pageResponse.data.hits);
          } catch (pageError) {
            console.error(`Failed to load page ${i}:`, pageError);
            failedPages.push(i);
          }
          if (isMountedRef.current) {
            setProgress(Math.round((i / totalPages) * 100));
          }
        }
      } finally {
        await MediaRelationsService.cleanupMediaListSession(companyId);
      }

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setMedialistsData(response.data.hits);

        if (failedPages.length > 0) {
          setFailedPages(failedPages);
          setFeedbackMsg(`Loaded ${response.data.hits.length} media lists, but page(s) ${failedPages.join(', ')} could not be fetched. Some lists may be missing.`);
          setAlertSeverity('warning');
        } else {
          setFailedPages([]);
          setFeedbackMsg(`Successfully loaded ${response.data.hits.length} media lists.`);
          setAlertSeverity('success');
        }
        setOpenSnackBar(true);
        setHasError(false);

        // Mark that we've successfully fetched data
        markApiAttempt('medialists');
      }

      return response.data.hits;
    } catch (error) {
      console.error("Error in getListsPage API call:", error);
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setAlertSeverity("error");
        setFeedbackMsg(`Error downloading contacts: ${error.message}, please try again or contact support.`);
        setErrorMessage(error.message || 'Unknown error');
        setOpenSnackBar(true);
        setHasError(true);
      }
      throw error;
    } finally {
      isRequestInProgressRef.current = false;
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      if (isMountedRef.current) {
        setLoadingMessage('');
        setIsLoading(false);
        setLoading(false);
      }
    }
  }, [companyId, setLoading, setMedialistsData, markApiAttempt]);

  // Effect for initial setup
  useEffect(() => {
    const jwtToken = getJwtToken();
    setCopyToCompanyId("");
    setIsFetching(false);
    
    if (jwtToken) {
      const { mediarelationspermissions } = jwt_decode(jwtToken);
      setMediarelationspermissions(mediarelationspermissions);
    }

    // Track component mount status
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      isRequestInProgressRef.current = false;
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    };
  }, []);

  // Separate effect for data loading
  useEffect(() => {
    if (!load || !companyId) {
      return;
    }

    async function loadData() {
      // Check if we have cached data
      if (medialistsData) {
        console.log("Using cached media lists data");
        setIsLoading(false);
        return;
      }
      
      // Check if this component has already attempted to load data and had an error
      if (hasError && apiAttempts.medialists) {
        console.log("Skipping API call due to previous error");
        return;
      }
      
      // Prevent multiple requests
      if (isRequestInProgressRef.current) {
        console.log("Request already in progress, skipping");
        return;
      }
      
      // Fetch new data if needed
      try {
        console.log("No cached data found, fetching fresh data");
        await fetchData();
      } catch (error) {
        console.error("Error loading media lists data:", error);
      }
    }
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, companyId, medialistsData, hasError, apiAttempts.medialists]);

  const resetErrorState = () => {
    console.log("Resetting media lists error state to allow retry");
    setHasError(false);
    resetApiAttempt('medialists'); // Reset the API attempt tracking
  };

  const refreshData = async () => {
    // Prevent multiple refresh calls
    if (isRequestInProgressRef.current) {
      console.log("Refresh already in progress, skipping");
      return;
    }

    try {
      resetApiAttempt('medialists'); // Reset API attempt for refresh
      setHasError(false); // Clear any previous errors
      setFailedPages([]);
      setLoadingMessage('Refreshing media lists...');
      loadingTimerRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          setLoadingMessage('Please wait, downloading lists may take some time...');
        }
      }, 10000);
      
      await fetchData();
      
      if (isMountedRef.current) {
        setFeedbackMsg('Data refreshed successfully.');
        setAlertSeverity('success');
        setOpenSnackBar(true);
      }
    } catch (error) {
      console.error("Error refreshing media lists:", error);
      if (isMountedRef.current) {
        setHasError(true); // Track error state
        const errorMsg = error.message || 'Unknown error';
        setErrorMessage(errorMsg);
        setFeedbackMsg(`Error refreshing data: ${errorMsg}, please try again or contact support.`);
        setAlertSeverity('error');
        setOpenSnackBar(true);
      }
    }
  };

  const handleExportData = () => {
    const exportdata = medialistsData.map((oneitem) => {
      return {
        id: oneitem.key,
        name: oneitem.name,
        journalistCount: oneitem.journalistCount,
        created: `${new Date(oneitem.inserted_at)}`,
        createdByFirstName: oneitem.created_by_first_name,
        createdByLastName: oneitem.created_by_last_name,
        updated: `${new Date(oneitem.updated_at)}`,
        lastUpdatedByFirstName: oneitem.last_updated_by_first_name,
        lastUpdatedByLastName: oneitem.last_updated_by_last_name,
        distributions: oneitem.distributions,
        openRate: oneitem.openRate,
        clickToOpenRate: oneitem.clickRate,
        unsubscribeRate: oneitem.unsubscribeRate
      }
    });
    
    const data = {
      data: exportdata,
      delimiter: ',',
      filename: `Media Lists for ${companyId}`,
    };
    
    csvDownload(data);
  };

  const exportContacts = async (rows, includePublic, isAdmin = false) => {
    handleCloseSnackBar();
    setIsFetching(true);
    // Backend only uses key and name per list (plus the top-level includePublic).
    let payload = {
      mediaLists: rows.map(row => ({ key: row.original.key, name: row.original.name })),
      includePublic: includePublic,
    };
    let zipFileName = includePublic || isAdmin ? "exported-contacts" : "exported-private-contacts";
    try {
      const onProgress = (batchNumber, totalBatches) => {
        setAlertSeverity("info");
        setFeedbackMsg(`Exporting contacts… (${batchNumber}/${totalBatches})`);
        setOpenSnackBar(true);
      };
      const response = await MediaRelationsService.exportContacts(payload, companyId, isAdmin, onProgress);
      await MediaRelationsService.saveCsv(response, zipFileName);
      
      setAlertSeverity("success");
      setFeedbackMsg("Successfully exported contacts.");
      setOpenSnackBar(true);
    } catch (error) {
      setAlertSeverity("error");
      setFeedbackMsg(`Error exporting contacts: ${error.message}`);
      setOpenSnackBar(true);
    }
    setIsFetching(false);
  };

  const copyContacts = async () => {
    handleCloseSnackBar();
    setIsFetching(true);
    let payload = { mediaLists: mediaListsToCopy };
    let zipFileName = "private-contacts";
    try {
      const response = await MediaRelationsService.copyContacts(payload, companyId, copyToCompanyId);
      await MediaRelationsService.saveCsv(response, zipFileName);
      
      setAlertSeverity("success");
      setFeedbackMsg(`Successfully copied contacts to company ${copyToCompanyId}`);
      setOpenSnackBar(true);
      handleCloseCopyListToCompanyModal();
    } catch (error) {
      setAlertSeverity("error");
      setFeedbackMsg(`Error copying contacts to company ${copyToCompanyId}: ${error.message}`);
      setOpenSnackBar(true);
    }
    setIsFetching(false);
  };

  const handleOpenCopyListToCompanyModal = async (rows) => {
    setMediaListsToCopy(rows.map(row => row.original));
    setCopyListToCompanyModalOpen(true);
    setCopyToCompanyId('');
  };

  const handleCloseCopyListToCompanyModal = async () => {
    setCopyListToCompanyModalOpen(false);
    setCopyToCompanyId('');
    setMediaListsToCopy([]);
  };

  const handleCloseSnackBar = () => {
    setOpenSnackBar(false);
    setAlertSeverity("info");
    setFeedbackMsg('');
  };

  const modalStyle = {
    backgroundColor: '#ffffff',
    padding: '20px',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: "80%",
    boxShadow: 24,
    p: 4,
    height: "80%",
  };

  // Format date in consistent way across components
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
  };

  // State for guide visibility
  const [showGuide, setShowGuide] = useState(false);
  const tableContainerRef = useRef(null);

  // Custom toolbar that directly overrides the standard DataTable toolbar
  const CustomToolbar = ({ table }) => {
    return (
      <div className={styles.customToolbarContainer}>
        {/* Top row with back button and primary export functions */}
        <div className={styles.topToolbarRow}>
          <Tooltip title="Previous page">
            <IconButton onClick={() => setActiveTab(null)} className={styles.backButton}>
              <ArrowBackIcon className={styles.backIcon} />
            </IconButton>
          </Tooltip>
          
          <FluxButton
            tint="primary"
            type="filled"
            onClick={handleExportData}
            style={{ marginRight: '8px' }}
          >
            Export List Overview Data
          </FluxButton>
          
          <FluxButton
            tint="accent"
            type="filled"
            onClick={() => exportContacts(table.getSelectedRowModel().rows, includePublic)}
            disabled={
              !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
            }
            style={{ marginRight: '8px' }}
          >
            Export Contacts
          </FluxButton>
          
          {mediarelationspermissions.includes("admin") &&
            <FluxButton
              tint="primary"
              type="filled"
              onClick={() => exportContacts(table.getSelectedRowModel().rows, includePublic, true)}
              disabled={
                !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
              }
              style={{ marginRight: '8px' }}
            >Export All Contacts (Admin)</FluxButton>
          }
          <FluxButton
            tint="secondary"
            type="filled"
            onClick={refreshData}
            style={{ marginRight: '8px' }}
          >
            Refresh Data
          </FluxButton>
          
          <FluxCheckbox
            id="includePublic"
            onFluxChange={(evt) => { setIncludePublic(evt.target.checked) }}
            style={{ marginRight: '8px' }}
          >
            Include Public Contacts
          </FluxCheckbox>
          
          <Tooltip title="Guide...">
            <IconButton 
              onClick={() => setShowGuide(true)}
              sx={{ color: '#188182', marginLeft: '4px', marginRight: '8px' }}
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        </div>

        {/* Bottom row with secondary actions */}
        <div className={styles.bottomToolbarRow}>
          {mediarelationspermissions.includes("write") &&
            <FluxButton
              tint="primary"
              type="filled"
              onClick={() => handleOpenCopyListToCompanyModal(table.getSelectedRowModel().rows)}
              disabled={
                (!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected())
              }
              style={{ marginRight: '8px' }}
            >Copy Contacts</FluxButton>
          }
          
          <Tooltip title="Info">
            <IconButton onClick={handleInfoClick} size="small">
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </div>
        
        <Popover
          open={openInfo}
          anchorEl={infoAnchorEl}
          onClose={handleInfoClose}
          disableRestoreFocus
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          slotProps={{
            paper: {
              style: { marginTop: '10px' }
            }
          }}
        >
          <Paper elevation={4} style={{ padding: '10px', backgroundColor: '#f0f0f0' }}>
            <Typography variant="body1">
              <ul>
                <li><strong>"Include Public Contacts"</strong> applies only to <strong>"Export Contacts"</strong></li>
                <li>When copying contacts, private contacts are not copied but exported as CSV files for each list to comply with data protection regulations.</li>
              </ul>
            </Typography>
          </Paper>
        </Popover>
      </div>
    );
  };

  const columns = useMemo(
    () => [
      { 
        accessorKey: 'key',
        header: 'ID',
        enableGlobalFilter: true 
      },
      { 
        accessorKey: 'name', 
        header: 'Name',
        enableGlobalFilter: true 
      },
      { 
        header: 'Created',
        accessorFn: (row) => formatDate(row.inserted_at),
        enableGlobalFilter: false 
      },
      { 
        header: 'Created By',
        accessorFn: (row) => `${row.created_by_first_name || ''} ${row.created_by_last_name || ''}`.trim(),
        enableGlobalFilter: true 
      },
      { 
        header: 'Updated',
        accessorFn: (row) => formatDate(row.updated_at),
        enableGlobalFilter: false 
      },
      { 
        header: 'Updated By',
        accessorFn: (row) => `${row.last_updated_by_first_name || ''} ${row.last_updated_by_last_name || ''}`.trim(),
        enableGlobalFilter: true 
      },
      { 
        accessorKey: 'journalistCount', 
        header: 'Journalist Count',
        enableGlobalFilter: false 
      },
      { 
        accessorKey: 'distributions', 
        header: 'Distributions',
        enableGlobalFilter: false 
      },
      { 
        accessorKey: 'openRate', 
        header: 'Open Rate',
        enableGlobalFilter: false 
      },
      { 
        accessorKey: 'clickRate', 
        header: 'Click Rate',
        enableGlobalFilter: false 
      },
      { 
        accessorKey: 'unsubscribeRate', 
        header: 'Unsubscribe Rate',
        enableGlobalFilter: false 
      }
    ],
    [],
  );

  if (!mediarelationspermissions || !mediarelationspermissions.includes('read')) {
    return <div>You do not have permission to access this page.</div>;
  }

  return (
    <div className={styles.Search}>
        {isFetching && (
          <Box sx={{ width: '100%' }}>
            <LinearProgress color="secondary" />
          </Box>
        )}

        {/* Show TableGuide when enabled */}
        {showGuide && (
          <TableGuide
            onClose={() => setShowGuide(false)}
            tableRef={tableContainerRef}
            customSteps={medialistsGuideSteps}
          />
        )}

        {failedPages.length > 0 && (
          <Alert
            severity="warning"
            onClose={() => setFailedPages([])}
            sx={{ mb: 1 }}
          >
            Some data could not be loaded (page{failedPages.length > 1 ? 's' : ''} {failedPages.join(', ')} timed out after retries). The list below may be incomplete. Use <strong>Refresh Data</strong> to try again.
          </Alert>
        )}

        <DataTable
          columns={columns}
          data={medialistsData || []}
          isLoading={isLoading && !hasError}
          loadingMessage={loadingMessage || `Downloading Lists... ${progress}%`}
          hasError={hasError}
          errorMessage={errorMessage}
          errorTitle="Error Loading Media Lists"
          onBackClick={() => setActiveTab(null)}
          onTryAgain={() => {
            resetErrorState();
            refreshData();
          }}
          renderToolbarActions={() => null}
          backTooltip="Return to Dashboard"
          showGuide={false}
          tableContainerRef={tableContainerRef}
          snackbarProps={{
            open: openSnackBar,
            onClose: handleCloseSnackBar,
            message: feedbackMsg,
            severity: alertSeverity,
            autoHideDuration: 10000
          }}
          tableProps={{
            muiSearchTextFieldProps: {
              placeholder: 'Search media lists...',
              variant: 'standard',
              sx: { width: '300px' }
            },
            initialState: { 
              density: 'compact',
              columnVisibility: { 
                key: true, 
                'Created': false, 
                'Created By': false, 
                'Updated': false, 
                'Updated By': false 
              },
              pagination: { pageSize: 50 }
            },
            // Here's the key for custom toolbar
            renderTopToolbarCustomActions: (tableProps) => <CustomToolbar table={tableProps.table} />
          }}
        />

      <Modal
        open={copyListToCompanyModalOpen}
        onClose={handleCloseCopyListToCompanyModal}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box style={{ ...modalStyle, width: "30%", height: "auto" }}>
          <Stack direction='row' spacing={4} justifyContent='flex-end' alignContent={"right"}>
            <FluxButton onClick={handleCloseCopyListToCompanyModal} type='text'>
              Cancel
            </FluxButton>
          </Stack>
          <Stack direction='column' spacing={1} justifyContent='flex-start' alignItems='flex-start'>
            <InputLabel>Target Company ID</InputLabel>
            <Input fullWidth type="text" onChange={(evt) => {setCopyToCompanyId(evt.target.value) }}></Input>
          </Stack>
          <Stack style={{}} direction='row' paddingTop={2} spacing={4} justifyContent='flex-end' alignItems='flex-end'>
            <FluxButton disabled={!(copyToCompanyId.length === 24) || copyToCompanyId === companyId} tint="primary" type="filled" onClick={() => {copyContacts()}}>
              Submit
            </FluxButton>
          </Stack>
        </Box>
      </Modal>
    </div>
  );
};

export default MediaLists;
